import React, { type ReactNode, useMemo, useState } from "react";

import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import BalanceOutlinedIcon from "@mui/icons-material/BalanceOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";
import DonutSmallOutlinedIcon from "@mui/icons-material/DonutSmallOutlined";
import FormatShapesOutlinedIcon from "@mui/icons-material/FormatShapesOutlined";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
// import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import UpdateOutlinedIcon from "@mui/icons-material/UpdateOutlined";
import WallpaperOutlinedIcon from "@mui/icons-material/WallpaperOutlined";
import WaterfallChartOutlinedIcon from "@mui/icons-material/WaterfallChartOutlined";
import { Box, Chip, IconButton, Typography } from "@mui/material";
import { HorizontalNavBar } from "components";
import type { DroppingItem } from "components/Report-generator/context/ReportGenerator-context";
import { type Layout, LAYOUTS } from "components/Report-generator/utils";
import { defaultGridDimensions } from "components/Report-generator/utils/gridUtils";
import { useFormatDateHook } from "hooks";
import type { CanvasCard, CardType, DateRangeValue, View } from "store/types";

import { BackgroundDrawer } from "./BackgroundDrawer";
import { SaveTemplateDialog } from "./SaveTemplateDialog";

const PERIOD_LABELS: Partial<Record<DateRangeValue["value"], string>> = {
  lastCalendarMonth: "Last Calendar Month",
  lastCalendarQuarter: "Last Calendar Quarter",
  lastYear: "Last Year",
  ytd: "YTD",
  last3Years: "Last 3 Years",
  last5Years: "Last 5 Years",
  inceptionToLastNAV: "Inception to Last NAV",
  today: "Today"
};

export type SidebarTab = "views" | "templates" | "components";

/** Create a clipped drag ghost so rounded corners are preserved. */
function setDragImage(e: React.DragEvent) {
  const el = e.currentTarget as HTMLElement;
  const clone = el.cloneNode(true) as HTMLElement;
  Object.assign(clone.style, {
    overflow: "hidden",
    borderRadius: "12px",
    width: `${el.offsetWidth}px`,
    position: "fixed",
    top: "-9999px"
  });
  document.body.appendChild(clone);
  e.dataTransfer.setDragImage(clone, el.offsetWidth / 2, el.offsetHeight / 2);
  requestAnimationFrame(() => clone.remove());
}

const PALETTE: { type: CardType; label: string; icon: React.ElementType; desc: string }[] = [
  { type: "chart", label: "Graph / Chart", icon: InsertChartOutlinedIcon, desc: "Pie, bar, or line chart" },
  { type: "table", label: "Data Table", icon: TableChartOutlinedIcon, desc: "Deal-level table" },
  // { type: "summary", label: "Summary Card", icon: GridViewOutlinedIcon, desc: "Compact metric grid" },
  { type: "kpi", label: "KPI Card", icon: TrendingUpOutlinedIcon, desc: "Large metric display" },
  {
    type: "text",
    label: "Text Card",
    icon: FormatShapesOutlinedIcon,
    desc: "Narrative commentary, or press Space to ask AI"
  },
  { type: "exposure", label: "Exposure Breakdown", icon: DonutSmallOutlinedIcon, desc: "Concentration by dimension" },
  { type: "cashflow", label: "Cash Flow Timeline", icon: WaterfallChartOutlinedIcon, desc: "Calls vs distributions" },
  { type: "unfunded", label: "Unfunded Commitments", icon: PendingActionsOutlinedIcon, desc: "Remaining commitments" },
  {
    type: "performance",
    label: "Performance Attribution",
    icon: BarChartOutlinedIcon,
    desc: "Top performers by metric"
  },
  { type: "benchmark", label: "Benchmark Comparison", icon: BalanceOutlinedIcon, desc: "Portfolio vs benchmark" },
  { type: "dataFreshness", label: "Data Freshness", icon: UpdateOutlinedIcon, desc: "Data as-of dates" }
  // { type: "macroBrief", label: "Macro Brief", icon: PublicOutlinedIcon, desc: "Market context snapshot" }
];

interface Props {
  views: View[];
  activeViewId: string | null;
  activeTab: SidebarTab;
  hasCards: boolean;
  onTabChange: (tab: SidebarTab) => void;
  onLoadView: (view: View) => void;
  onDeleteView: (id: string) => void;
  onSaveView: (name: string, description?: string) => Promise<void>;
  onUpdateView: (name?: string, description?: string) => Promise<void>;
  onAutoClean: () => void;
  onLoadLayout: (cards: CanvasCard[]) => void;
  onClearCanvas: () => void;
  setDroppingItem: (item: DroppingItem | null) => void;
  backgroundUrl: string;
  backgroundOpacity: number;
  onBackgroundApply: (url: string, opacity: number, file: File | null) => void;
  onBackgroundRemove: () => void;
}

export const LeftSidebar: React.FC<Props> = ({
  views,
  activeViewId,
  activeTab,
  hasCards,
  onTabChange,
  onLoadView,
  onDeleteView,
  onSaveView,
  onUpdateView,
  onAutoClean,
  onLoadLayout,
  onClearCanvas,
  setDroppingItem,
  backgroundUrl,
  backgroundOpacity,
  onBackgroundApply,
  onBackgroundRemove
}) => {
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [backgroundOpen, setBackgroundOpen] = useState(false);

  const activeView = useMemo(
    () => (activeViewId ? views.find((v) => v.id === activeViewId) : undefined),
    [views, activeViewId]
  );

  const handleDialogSave = async (name: string, description?: string) => {
    setSaving(true);
    try {
      await onSaveView(name, description);
    } finally {
      setSaving(false);
    }
  };

  const handleDialogUpdate = async (_id: number, name: string, description?: string) => {
    setSaving(true);
    try {
      await onUpdateView(name, description);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        width: 320,
        flexShrink: 0,
        bgcolor: "#FFFFFF",
        borderRadius: "8px",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden"
      }}
    >
      {/* Tab bar */}
      <Box sx={{ flexShrink: 0, px: "16px", py: "8px" }}>
        <HorizontalNavBar
          value={activeTab}
          onChange={(_, value) => onTabChange(value as SidebarTab)}
          tabs={[
            { value: "components", label: "Components" },
            { value: "templates", label: "Templates" },
            { value: "views", label: views.length > 0 ? `Views (${views.length})` : "Views" }
          ]}
        />
      </Box>

      {/* Tab content */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto"
        }}
      >
        {activeTab === "views" && (
          <Box sx={{ p: "14px 14px 64px" }}>
            <Box
              component="button"
              onClick={() => setSaveDialogOpen(true)}
              sx={{
                width: "100%",
                p: "10px 0",
                mb: "12px",
                bgcolor: activeViewId ? "#071f35" : "#E8F5F0",
                border: activeViewId ? "1px solid #071f35" : "1px dashed #496682",
                borderRadius: "12px",
                color: activeViewId ? "#FFFFFF" : "#071f35",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
                transition: "background-color 0.15s",
                "&:hover": { bgcolor: activeViewId ? "#0a2e4f" : "#d5ebe5" }
              }}
            >
              {activeViewId ? "Save / Update view" : "Save current view"}
            </Box>

            <SaveTemplateDialog
              open={saveDialogOpen}
              onClose={() => setSaveDialogOpen(false)}
              onSave={handleDialogSave}
              onUpdate={activeViewId ? handleDialogUpdate : undefined}
              saving={saving}
              currentTemplateId={activeViewId ? parseInt(activeViewId, 10) : null}
              currentTemplateName={activeView?.name}
              currentTemplateDescription={activeView?.description}
            />

            {!views.length ? (
              <EmptyState
                title="No saved views yet"
                body="Set up filters, cards, and page layout, then save the current report as a reusable view."
              />
            ) : (
              views.map((view) => (
                <ViewItem
                  key={view.id}
                  view={view}
                  isActive={activeViewId === view.id}
                  onLoad={() => onLoadView(view)}
                  onDelete={() => onDeleteView(view.id)}
                />
              ))
            )}
          </Box>
        )}

        {activeTab === "templates" && (
          <Box sx={{ pt: "14px", pb: "64px" }}>
            <SectionLabel>Report Templates</SectionLabel>
            {LAYOUTS.map((layout) => (
              <LayoutItem key={layout.id} layout={layout} onLoad={() => onLoadLayout(layout.build())} />
            ))}
          </Box>
        )}

        {activeTab === "components" && (
          <Box
            sx={{
              pt: "14px",
              pb: "64px"
            }}
          >
            <SectionLabel>Drag onto canvas</SectionLabel>
            <Box>
              {PALETTE.map((item) => (
                <Box
                  key={item.type}
                  draggable
                  unselectable="on"
                  onDragStart={(e) => {
                    e.dataTransfer.setData("cardType", item.type);
                    const { gridW, gridH } = defaultGridDimensions(item.type);
                    setDroppingItem({ i: "__dropping__", w: gridW, h: gridH });
                    setDragImage(e);
                  }}
                  onDragEnd={() => setDroppingItem(null)}
                  sx={{
                    m: "0 12px 6px",
                    p: "10px 12px",
                    borderRadius: "12px",
                    border: "1px solid #E1E8F0",
                    cursor: "grab",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    bgcolor: "#FFFFFF",
                    userSelect: "none",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      borderColor: "#7B92A9",
                      bgcolor: "#F6F8FC",
                      boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.08)"
                    }
                  }}
                >
                  <item.icon sx={{ fontSize: 20, color: "#496682" }} />
                  <Box>
                    <Typography sx={{ fontSize: 15, lineHeight: 1, fontWeight: 700, color: "#071f35" }}>
                      {item.label}
                    </Typography>
                    <Typography sx={{ fontSize: 12, lineHeight: 1, color: "#92a7bc", mt: "2px" }}>
                      {item.desc}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            <SectionLabel sx={{ mt: "10px" }}>Tools</SectionLabel>
            <ToolItem
              icon={AutoFixHighOutlinedIcon}
              label="Auto-clean"
              desc="Reset card widths"
              onClick={onAutoClean}
            />
            <ToolItem
              icon={WallpaperOutlinedIcon}
              label="Background image"
              desc={backgroundUrl ? "Change or remove background" : "Add a page background"}
              onClick={() => setBackgroundOpen(true)}
            />
            {hasCards && (
              <ToolItem
                icon={DeleteSweepOutlinedIcon}
                label="Clear canvas"
                desc="Remove all cards"
                onClick={onClearCanvas}
              />
            )}
          </Box>
        )}
      </Box>
      <BackgroundDrawer
        open={backgroundOpen}
        currentBackgroundUrl={backgroundUrl}
        currentOpacity={backgroundOpacity}
        onClose={() => setBackgroundOpen(false)}
        onApply={(url, opacity, file) => {
          onBackgroundApply(url, opacity, file);
          setBackgroundOpen(false);
        }}
        onRemove={() => {
          onBackgroundRemove();
          setBackgroundOpen(false);
        }}
      />
    </Box>
  );
};

interface EmptyStateProps {
  title: string;
  body: string;
}

const EmptyState: React.FC<EmptyStateProps> = ({ title, body }) => {
  return (
    <Box
      sx={{
        textAlign: "center",
        p: "32px 18px",
        borderRadius: "14px",
        border: "1px dashed #dbe8f5",
        bgcolor: "#f6f8fc"
      }}
    >
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#071f35", mb: "6px" }}>{title}</Typography>
      <Typography sx={{ fontSize: 12, color: "#92a7bc", lineHeight: 1.5 }}>{body}</Typography>
    </Box>
  );
};

interface LayoutItemProps {
  layout: Layout;
  onLoad: () => void;
}

const LayoutItem: React.FC<LayoutItemProps> = ({ layout, onLoad }) => {
  const colors = ["#E8F5F0", "#D6F4EF", "#f6f8fc"];

  return (
    <Box
      component="button"
      onClick={onLoad}
      sx={{
        m: "0 12px 8px",
        p: "10px 12px",
        borderRadius: "12px",
        border: "1px solid #dbe8f5",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: "12px",
        bgcolor: "#FFFFFF",
        width: "calc(100% - 24px)",
        textAlign: "left",
        transition: "all 0.12s",
        "&:hover": { borderColor: "#7B92A9", bgcolor: "#F6F8FC" }
      }}
    >
      <Box sx={{ flexShrink: 0, width: 44, display: "flex", flexDirection: "column", gap: "2px" }}>
        {layout.rows.map((row, ri) => (
          <Box key={ri} sx={{ display: "flex", gap: "2px" }}>
            {row.map((span, ci) => (
              <Box
                key={ci}
                sx={{
                  flex: span,
                  height: 9,
                  borderRadius: "2px",
                  bgcolor: colors[ci % colors.length],
                  border: "1px solid rgba(7,31,53,0.12)"
                }}
              />
            ))}
          </Box>
        ))}
      </Box>
      <Box>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#071f35" }}>{layout.label}</Typography>
        <Typography sx={{ fontSize: 10, color: "#92a7bc", mt: "2px" }}>{layout.desc}</Typography>
      </Box>
    </Box>
  );
};

interface SectionLabelProps {
  children: ReactNode;
  sx?: Record<string, unknown>;
}

const SectionLabel: React.FC<SectionLabelProps> = ({ children, sx: sxProp }) => {
  return (
    <Typography
      sx={{
        p: "0 16px 8px",
        fontSize: 14,
        lineHeight: "16px",
        fontWeight: 400,
        color: "#92a7bc",
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        ...sxProp
      }}
    >
      {children}
    </Typography>
  );
};

interface ToolItemProps {
  icon: React.ElementType;
  label: string;
  desc: string;
  onClick: () => void;
}

const ToolItem: React.FC<ToolItemProps> = ({ icon: Icon, label, desc, onClick }) => {
  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        m: "0 12px 6px",
        p: "10px 12px",
        borderRadius: "12px",
        border: "1px dashed #dbe8f5",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: "10px",
        bgcolor: "transparent",
        width: "calc(100% - 24px)",
        textAlign: "left",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          borderColor: "#7B92A9",
          bgcolor: "#F6F8FC",
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.08)"
        }
      }}
    >
      <Icon sx={{ fontSize: 20, color: "#496682" }} />
      <Box>
        <Typography sx={{ fontSize: 15, lineHeight: 1, fontWeight: 700, color: "#071f35" }}>{label}</Typography>
        <Typography sx={{ fontSize: 12, lineHeight: 1, color: "#92a7bc", mt: "2px" }}>{desc}</Typography>
      </Box>
    </Box>
  );
};

interface ViewItemProps {
  view: View;
  isActive: boolean;
  onLoad: () => void;
  onDelete: () => void;
}

const ViewItem: React.FC<ViewItemProps> = ({ view, isActive, onLoad, onDelete }) => {
  const formatDate = useFormatDateHook();
  const { investors } = view.filters;
  // Asset Class hidden: was investors.length > 0 || assetClasses.length > 0
  const hasFilters = investors.length > 0;

  const periodLabel = (() => {
    if (!view.period) return "N/A";
    const { value, from, to } = view.period;
    const label = PERIOD_LABELS[value];
    if (label) return label;
    if (value === "fromInception") {
      return from ? `From Inception (${formatDate({ date: from })})` : "From Inception";
    }
    if ((value === "dateRange" || value === "selectYear" || value === "selectQuarter") && from && to) {
      return `${formatDate({ date: from })} - ${formatDate({ date: to })}`;
    }
    return value;
  })();

  return (
    <Box
      onClick={onLoad}
      sx={{
        p: "12px",
        mb: 1,
        borderRadius: "14px",
        border: `1px solid ${isActive ? "#071F35" : "#dbe8f5"}`,
        bgcolor: isActive ? "#E8F5F0" : "#FFFFFF",
        cursor: "pointer",
        transition: "border-color 0.15s, background-color 0.15s",
        "&:hover": {
          borderColor: isActive ? "#071F35" : "#b0c4de",
          bgcolor: isActive ? "#E8F5F0" : "#f6f8fc"
        },
        display: "flex",
        flexDirection: "column",
        gap: "6px"
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#071f35" }}>{view.name}</Typography>
        <IconButton
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          sx={{ color: "#92a7bc", p: "2px", "&:hover": { color: "#071f35" } }}
          aria-label={`Delete ${view.name}`}
        >
          <DeleteIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>

      {hasFilters ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          {investors.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px", alignItems: "center" }}>
              <Typography component="span" sx={{ fontSize: 10, fontWeight: 700, color: "#92a7bc" }}>
                Investor:
              </Typography>
              {investors.slice(0, 2).map((tag) => (
                <Chip key={tag} label={tag} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 600 }} />
              ))}
              {investors.length > 2 && (
                <Typography component="span" sx={{ fontSize: 10, color: "#92a7bc" }}>
                  +{investors.length - 2} more
                </Typography>
              )}
            </Box>
          )}
          {/* Asset Class section hidden for now (was: assetClasses chips) */}
        </Box>
      ) : (
        <Typography sx={{ fontSize: 10, color: "#92a7bc" }}>No filters</Typography>
      )}

      <Typography sx={{ fontSize: 10, color: "#92a7bc" }}>
        {view.cards.length} cards | {view.orientation === "landscape" ? "Horizontal" : "Vertical"} | {view.currency} |{" "}
        {periodLabel}
      </Typography>
    </Box>
  );
};
